import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

import { ApiError, makeClient } from '../api/client'
import { useAuth } from '../auth/AuthContext'

type Tenant = {
  id: string
  slug: string
  display_name: string
  status: 'active' | 'suspended' | 'disabled'
  data_region: string
  config: Record<string, unknown>
  created_at: string
  updated_at: string
}

const QUERY_KEY = ['admin', 'tenant'] as const

export default function AdminTenantPage() {
  const { accessToken } = useAuth()
  const client = useMemo(() => makeClient(() => accessToken), [accessToken])
  const qc = useQueryClient()

  const tenantQ = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => client.call<Tenant>('/api/v1/admin/tenant'),
  })

  const [displayName, setDisplayName] = useState('')
  const [configJson, setConfigJson] = useState('{}')
  const [configError, setConfigError] = useState<string | null>(null)

  useEffect(() => {
    if (tenantQ.data) {
      setDisplayName(tenantQ.data.display_name)
      setConfigJson(JSON.stringify(tenantQ.data.config ?? {}, null, 2))
    }
  }, [tenantQ.data])

  const saveMut = useMutation({
    mutationFn: async () => {
      let parsedConfig: Record<string, unknown> | undefined
      if (configJson.trim() !== '') {
        try {
          parsedConfig = JSON.parse(configJson) as Record<string, unknown>
        } catch {
          throw new ApiError(0, 'invalid_json', 'Config must be valid JSON.')
        }
      }
      return client.call<Tenant>('/api/v1/admin/tenant', {
        method: 'PATCH',
        body: {
          display_name: displayName.trim(),
          config: parsedConfig,
        },
      })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QUERY_KEY })
    },
  })

  function validateConfigOnEdit(v: string) {
    setConfigJson(v)
    if (v.trim() === '') {
      setConfigError(null)
      return
    }
    try {
      JSON.parse(v)
      setConfigError(null)
    } catch (e) {
      setConfigError(e instanceof Error ? e.message : 'Invalid JSON')
    }
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Stack spacing={2}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Tenant settings
        </Typography>

        {tenantQ.isLoading && <Typography variant="body2">Loading…</Typography>}
        {tenantQ.isError && (
          <Alert severity="error">
            {tenantQ.error instanceof Error ? tenantQ.error.message : 'Failed to load tenant.'}
          </Alert>
        )}

        {tenantQ.data && (
          <>
            <Stack direction="row" spacing={2} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
              <Chip label={`slug: ${tenantQ.data.slug}`} size="small" />
              <Chip label={`status: ${tenantQ.data.status}`} size="small"
                    color={tenantQ.data.status === 'active' ? 'success' : 'warning'} />
              <Chip label={`region: ${tenantQ.data.data_region}`} size="small" />
              <Chip label={`id: ${tenantQ.data.id.slice(0, 8)}…`} size="small" variant="outlined" />
            </Stack>

            <TextField
              label="Display name"
              fullWidth
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              helperText="Shown across the product wherever the tenant is named."
            />

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Config (JSON)</Typography>
              <TextField
                fullWidth
                multiline
                minRows={8}
                maxRows={20}
                value={configJson}
                onChange={(e) => validateConfigOnEdit(e.target.value)}
                error={!!configError}
                helperText={configError ?? 'Free-form tenant configuration — brand overrides, feature flags, integration hints.'}
                slotProps={{ input: { sx: { fontFamily: 'monospace', fontSize: '0.85rem' } } }}
              />
            </Box>

            {saveMut.error && (
              <Alert severity="error">
                {saveMut.error instanceof ApiError ? saveMut.error.message : 'Save failed.'}
              </Alert>
            )}
            {saveMut.isSuccess && (
              <Alert severity="success">Tenant updated.</Alert>
            )}

            <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => saveMut.mutate()}
                disabled={saveMut.isPending || !!configError || !displayName.trim()}
              >
                {saveMut.isPending ? 'Saving…' : 'Save changes'}
              </Button>
            </Stack>

            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Last updated {new Date(tenantQ.data.updated_at).toLocaleString('en-GB')}
            </Typography>
          </>
        )}
      </Stack>
    </Paper>
  )
}
